import { motion } from 'framer-motion';
import { Flame, Megaphone, Instagram, Clock3 } from 'lucide-react';
import { CloudText, Reveal, AltitudeMarker } from '@/components/CloudText';
import { PriceBadge, IncludesList, RainNote } from '@/components/PricingBits';
import { SERVICES, STARTER_PACK } from '@/data/content';
import { scrollToSection } from '@/lib/scroll';

function ServiceCard({ s }) {
  return (
    <motion.article whileHover={{ y: -8 }} transition={{ type: 'spring', stiffness: 200, damping: 18 }} className="glass rounded-[1.75rem] p-7 h-full flex flex-col group" data-testid={`service-card-${s.id}`} data-cursor="cloud">
      <div className="flex items-start justify-between gap-4">
        <Flame size={18} className="text-[#f97316] mt-2 drop-shadow-[0_0_8px_rgba(249,115,22,0.7)]" />
        <PriceBadge price={s.price} testId={`service-price-${s.id}`} />
      </div>
      <h3 className="font-display text-lg sm:text-xl font-semibold mt-5 tracking-tight uppercase">{s.name}</h3>
      <p className="mt-3 text-sm text-white/75 leading-relaxed">{s.desc}</p>
      <IncludesList label={s.listLabel} items={s.includes} cols={s.id === 'video' || s.id === 'banner' ? 2 : 1} />
      <div className="flex-1" />
      {s.requirement && <RainNote testId={`service-requirement-${s.id}`}>{s.requirement}</RainNote>}
      {s.revision && <RainNote kind="revision" testId={`service-revision-${s.id}`}>{s.revision}</RainNote>}
    </motion.article>
  );
}

function PromoCard({ s }) {
  return (
    <motion.article whileHover={{ y: -8 }} transition={{ type: 'spring', stiffness: 200, damping: 18 }} className="glass rounded-[1.75rem] p-7 h-full flex flex-col" data-testid="service-card-promo" data-cursor="cloud">
      <div className="flex items-start justify-between gap-4">
        <Megaphone size={18} className="text-[#f97316] mt-2 drop-shadow-[0_0_8px_rgba(249,115,22,0.7)]" />
        <PriceBadge price={s.price} testId="service-price-promo" />
      </div>
      <h3 className="font-display text-lg sm:text-xl font-semibold mt-5 tracking-tight uppercase">{s.name}</h3>
      <p className="mt-3 text-sm text-white/75 leading-relaxed">{s.desc}</p>
      <a href={s.handle.url} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-2 self-start rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white ring-1 ring-white/25 hover:bg-white/20 transition-colors" data-testid="promo-handle-link"><Instagram size={14} /> {s.handle.label}</a>
      <div className="mt-6">
        <p className="font-display text-[0.6rem] tracking-[0.35em] uppercase text-[#ffb37a]">How it works</p>
        <ol className="mt-3 grid sm:grid-cols-2 gap-3">
          {s.steps.map((st, i) => (
            <li key={st} className="flex items-start gap-3 text-sm text-white/85 leading-snug">
              <span className="font-display text-xs font-bold h-6 w-6 shrink-0 rounded-full bg-[#f97316]/90 text-white flex items-center justify-center shadow-[0_0_14px_rgba(249,115,22,0.5)]">{i + 1}</span>
              <span>{st}</span>
            </li>
          ))}
        </ol>
      </div>
      <p className="mt-5 flex items-start gap-2 text-sm text-white/85"><Clock3 size={15} className="mt-0.5 shrink-0 text-[#ffb37a]" /> {s.note}</p>
      <RainNote kind="revision" testId="service-important-promo">
        <span className="font-semibold text-white">Important — </span>{s.important}
      </RainNote>
    </motion.article>
  );
}

export function ServicesSection() {
  const wide = (id) => id === 'video' || id === 'promo';
  return (
    <section id="services" className="relative min-h-screen px-6 sm:px-[8vw] py-[18vh]" data-testid="services-section">
      <AltitudeMarker index="02" label="SERVICES" />
      <div className="absolute inset-x-[-10%] top-[10%] h-[80%] rounded-[50%] pointer-events-none" style={{ background: 'radial-gradient(ellipse, rgba(255,255,255,0.12), rgba(255,255,255,0) 65%)', filter: 'blur(40px)' }} />
      <div className="relative max-w-6xl">
        <p className="cloud-eyebrow mb-5">Altitude 02 — Designed in the clouds</p>
        <CloudText lines={['CUSTOM', 'CREATIVE SERVICES']} testId="services-title" />
        <Reveal delay={0.25}><p className="mt-6 text-base sm:text-lg text-white/75 max-w-2xl">Every service starts with your blueprint. Starting prices are listed on each cloud — includes, client requirements and revision policies are spelled out so there are no surprises.</p></Reveal>
        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-5 items-start">
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            {SERVICES.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.1} className={wide(s.id) ? 'sm:col-span-2' : ''}>
                {s.id === 'promo' ? <PromoCard s={s} /> : <ServiceCard s={s} />}
              </Reveal>
            ))}
          </div>
          <Reveal className="lg:col-span-5 lg:sticky lg:top-28" delay={0.25}>
            <motion.article whileHover={{ y: -10, scale: 1.01 }} transition={{ type: 'spring', stiffness: 160, damping: 18 }} className="glass-deep relative h-full rounded-[2.5rem] p-6 sm:p-12 overflow-hidden flex flex-col justify-between min-h-[420px] w-full max-w-full" style={{ boxShadow: '0 30px 90px rgba(3,12,36,0.5), 0 0 80px rgba(249,115,22,0.18), inset 0 1px 0 rgba(255,255,255,0.3)' }} data-testid="service-card-starter-pack" data-cursor="cloud">
              <div className="absolute -right-24 -top-24 w-72 h-72 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(249,115,22,0.35), rgba(249,115,22,0) 70%)', filter: 'blur(20px)' }} />
              <div>
                <div className="flex items-center gap-2 text-[#ffb37a]"><Flame size={18} className="drop-shadow-[0_0_10px_rgba(249,115,22,0.9)]" /><span className="font-display text-[0.65rem] tracking-[0.35em] uppercase">Most complete · Flat price</span></div>
                <p className="font-display text-5xl sm:text-7xl font-extrabold mt-8 text-white break-words" style={{ textShadow: '0 0 40px rgba(249,115,22,0.45)' }} data-testid="starter-pack-price">{STARTER_PACK.price}</p>
                <h3 className="font-display text-2xl sm:text-3xl font-bold mt-4 uppercase tracking-tight leading-tight">Content Creator<br />Package</h3>
                <p className="mt-5 text-base text-white/80 italic">“{STARTER_PACK.desc}”</p>
                <IncludesList items={STARTER_PACK.includes} testId="starter-pack-includes" />
                <p className="mt-5 text-sm text-white/60">{STARTER_PACK.long}</p>
                <RainNote kind="revision" testId="starter-pack-revision">{STARTER_PACK.revision}</RainNote>
              </div>
              <button type="button" onClick={() => scrollToSection('start')} className="btn-cloud primary mt-10 self-start" data-testid="starter-pack-cta">Start with the package</button>
            </motion.article>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
